import React, { memo } from 'react';

interface DiscoveryPaginationDotsProps {
  activeIndex: number;
  total: number;
}

/**
 * Functional UI component for pagination dots on Discovery cards.
 * Sits inside a top-center pill notch with a maximum of 4 dots.
 */
export const DiscoveryPaginationDots = memo<DiscoveryPaginationDotsProps>(({ 
  activeIndex, 
  total 
}) => {
  // Constraint: Maximum of 4 dots only
  const displayCount = Math.min(total, 4);
  
  if (displayCount <= 1) return null;

  // Logic: Map the active state to one of the 4 dots if total > 4
  // For simplicity, we cap the active index to the display count.
  const mappedActiveIndex = activeIndex % displayCount;

  return (
    <div 
      className="absolute flex justify-center pointer-events-none" 
      style={{ 
        top: '12px', 
        left: '50%', 
        transform: 'translateX(-50%)',
        zIndex: 1000,
        width: 'auto'
      }}
    >
      <div 
        className="flex items-center justify-center bg-black/60 px-3 py-1.5 rounded-full" 
        style={{ 
          backdropFilter: 'blur(16px)', 
          boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
          border: '1px solid rgba(255,255,255,0.1)',
          minHeight: '20px'
        }}
      >
        {Array.from({ length: displayCount }).map((_, i) => (
          <div 
            key={i} 
            // Aesthetic: Map 'active' class to index
            className={`transition-all duration-300 rounded-full mx-1 ${i === mappedActiveIndex ? 'active' : ''}`}
            style={{
              width: '6px',
              height: '6px',
              backgroundColor: 'white',
              // Aesthetic: 40% opacity for inactive and 100% for active
              opacity: i === mappedActiveIndex ? 1 : 0.4,
              boxShadow: i === mappedActiveIndex ? '0 0 6px rgba(255,255,255,0.9)' : 'none',
              transform: i === mappedActiveIndex ? 'scale(1.1)' : 'scale(1)'
            }}
          />
        ))}
      </div>
    </div>
  );
});
